import type { Referral } from '../../api/types';
import { UnlockIcon } from './RowIcons';
import { daysBetween, formatDate, formatMoney } from './referralDates';
import type { Sort, SortKey } from './referralSort';

interface Props {
  referrals: Referral[];

  /** Today as `yyyy-mm-dd`, for spotting rows whose probation is up and undecided. */
  today: string;

  /**
   * Days before probation clears that HR is written to. From the API rather than typed here, so this
   * flags exactly the rows the notice is about — see `ReferralSettingsResponse`.
   */
  noticeLeadDays: number;

  /** The column the table is ordered by, or null for the urgency order it loads in. */
  sort: Sort;
  onSort: (key: SortKey) => void;

  /** Ids currently being saved, so their controls stop accepting a second click. */
  deciding: ReadonlySet<string>;
  onDecide: (referral: Referral, eligible: boolean | null) => void;

  /** Closes one row out — paid or not paid, finished either way. */
  onClose: (referral: Referral, paid: boolean) => void;

  /** Unlocks a finished row — the one way past the lock a finished row carries. */
  onReopen: (referral: Referral) => void;

  /** A row whose payout date went by undecided asks its own question before accepting a yes. */
  onResolveMissed: (referral: Referral) => void;
  onEdit: (referral: Referral) => void;
}

/**
 * The spreadsheet, with the columns that were kept by hand in it worked out instead: the payout date
 * is looked up in the payroll calendar, and the referral count is counted across the log.
 *
 * The one thing that can be changed from here is the eligibility decision, because it is one click and
 * putting it behind the edit dialog is what leaves rows undecided. Everything else goes through Edit.
 */
export function ReferralTable({
  referrals,
  today,
  noticeLeadDays,
  sort,
  onSort,
  deciding,
  onDecide,
  onClose,
  onReopen,
  onResolveMissed,
  onEdit,
}: Props) {
  return (
    <div className="table-scroll">
      <table className="table table--referrals">
        <thead>
          <tr>
            <SortableHeader label="Team member" columnKey="referringEmployeeName" sort={sort} onSort={onSort} />
            <SortableHeader label="New hire" columnKey="newHireName" sort={sort} onSort={onSort} />
            <SortableHeader label="Job title" columnKey="jobTitle" sort={sort} onSort={onSort} />
            <SortableHeader label="Department" columnKey="department" sort={sort} onSort={onSort} />
            <SortableHeader label="Location" columnKey="location" sort={sort} onSort={onSort} />
            <SortableHeader label="Hire date" columnKey="hireDate" sort={sort} onSort={onSort} />
            <SortableHeader label="90 days" columnKey="probationClearedOn" sort={sort} onSort={onSort} />
            <SortableHeader label="Payout date" columnKey="payoutDate" sort={sort} onSort={onSort} />
            <SortableHeader label="Payout" columnKey="payoutAmount" className="table__number" sort={sort} onSort={onSort} />
            <SortableHeader label="Referrals" columnKey="referralCount" className="table__number" sort={sort} onSort={onSort} />
            <SortableHeader label="Eligible" columnKey="eligibleForPayout" className="table__center" sort={sort} onSort={onSort} />
            <SortableHeader label="Notified" columnKey="payoutAnnouncedToPayroll" className="table__center" sort={sort} onSort={onSort} />
            <SortableHeader label="Paid" columnKey="isPaid" className="table__center" sort={sort} onSort={onSort} />
            <SortableHeader label="Done" columnKey="completed" className="table__center" sort={sort} onSort={onSort} />
            <th scope="col" className="table__fit">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((referral) => {
            // Probation is up and nobody has decided — the row waiting on HR.
            const needsDecision =
              referral.eligibleForPayout === null &&
              !referral.completed &&
              referral.probationClearedOn <= today;

            // Worse than waiting: the run it was due on has been and gone with the decision still not
            // made. A different problem needing a different answer, so it is marked differently.
            const missed =
              needsDecision && referral.payoutDate !== null && referral.payoutDate < today;

            /**
             * Approaching the 90-day mark with no decision on it — the same window
             * `ReferralProbationNoticeService` mails HR about, read from the same setting so the two
             * cannot drift.
             *
             * Not quite the same list, and deliberately so: the mail also skips whatever it has
             * already sent, and this does not. "Coming due and undecided" stays true of a referral
             * after the message about it has gone, and that is the question the table answers.
             *
             * Deliberately **not** the same mark as `needsDecision`. That one means the date has
             * passed and the decision can be made now; this one means it has not, and there is
             * nothing to decide yet. One colour for both would stop amber meaning "act" and start it
             * meaning "act within a fortnight", which is how a highlight stops being read at all.
             */
            const daysToProbation = daysBetween(today, referral.probationClearedOn);

            const noticeSoon =
              !needsDecision &&
              referral.eligibleForPayout === null &&
              !referral.completed &&
              daysToProbation >= 0 &&
              daysToProbation <= noticeLeadDays;

            /**
             * The payout date has been and gone and the row is still open — the Closure list.
             *
             * Not "accepted and unpaid". Both answers close a referral, so what belongs here is every
             * row still to be answered for, the undecided included: those are the ones that otherwise
             * sit past their own payout date with nobody owning them.
             */
            const awaitingClosure =
              !referral.completed && referral.payoutDate !== null && referral.payoutDate <= today;

            /** A call is in flight for this row, so every control on it stops taking clicks. */
            const busy = deciding.has(referral.id);

            const rowClass = [
              referral.completed ? 'table__row--done' : '',
              missed
                ? 'table__row--overdue'
                : needsDecision
                  ? 'table__row--action'
                  : noticeSoon
                    ? 'table__row--soon'
                    : '',
            ]
              .filter(Boolean)
              .join(' ');

            /**
             * The whole row opens the edit dialog, so there is no pencil to aim at.
             *
             * Not on a finished row. The API refuses an edit to one, so a click that opened the form
             * there would be a form whose Save could only fail — reopening is the thing to do to a
             * closed referral, and it has its own button.
             */
            const openable = !referral.completed;

            const open = () => {
              // A click that ends a text selection is somebody copying a name out of the table, not
              // somebody asking for the form. Opening a dialog over the top of it loses the selection
              // and the intent with it.
              if (window.getSelection()?.toString() !== '') return;

              onEdit(referral);
            };

            return (
              <tr
                key={referral.id}
                className={rowClass === '' ? undefined : rowClass}
                // No `role="button"`: a row that claims to be a button stops being a row, and the
                // column headings stop being announced with its cells. Reachable by keyboard instead,
                // which is what the pencil was providing.
                tabIndex={openable ? 0 : undefined}
                title={openable ? `Edit the referral for ${referral.newHireName}` : undefined}
                onClick={openable ? open : undefined}
                onKeyDown={
                  openable
                    ? (e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;

                        // Space scrolls the page otherwise, and Enter would submit a form if this
                        // table ever sat inside one.
                        e.preventDefault();
                        onEdit(referral);
                      }
                    : undefined
                }
              >
                <td>{referral.referringEmployeeName}</td>

                <td>
                  <span className="table__strong">{referral.newHireName}</span>
                </td>

                <td>{referral.jobTitle}</td>

                <td>{referral.department ?? '—'}</td>

                <td>{referral.location ?? '—'}</td>

                <td>{formatDate(referral.hireDate)}</td>

                <td>
                  {formatDate(referral.probationClearedOn)}
                  {needsDecision && <span className="table__sub table__sub--action">passed</span>}

                  {/* The count, not just the fact: "in 2 days" and "in 13 days" are the same flag to
                      the mail but not to whoever has to get the approval signed. */}
                  {noticeSoon && (
                    <span className="table__sub table__sub--soon">
                      {daysToProbation === 0
                        ? 'today'
                        : daysToProbation === 1
                          ? 'in 1 day'
                          : `in ${daysToProbation} days`}
                    </span>
                  )}
                </td>

                <td
                  title={
                    referral.payoutDate === null
                      ? "The payout calendar does not reach this far — extend it on the Payout dates page."
                      : undefined
                  }
                >
                  <span className="table__strong">
                    {referral.payoutDate === null ? "—" : formatDate(referral.payoutDate)}
                  </span>
                </td>

                <td className="table__number">{formatMoney(referral.payoutAmount)}</td>

                <td className="table__number">{referral.referralCount}</td>

                {/* The cells that carry controls swallow the click rather than each button doing it,
                    so a control added here later cannot forget to. Their blank space stops opening the
                    form too, which is the predictable half of the bargain: a cell either belongs to its
                    buttons or to the row, not to both depending on where in it you land. */}
                <td className="table__center" onClick={(e) => e.stopPropagation()}>
                  <EligibilityControl
                    referral={referral}
                    needsDecision={needsDecision}
                    missed={missed}
                    busy={busy}
                    onDecide={onDecide}
                    onResolveMissed={onResolveMissed}
                  />
                </td>

                {/* Whether payroll has been told to pay this bonus, on the run it is currently on.
                    Nobody sets it — the digest job stamps it when the message actually goes out, which
                    is why it sits among the flags rather than among the things with a button.

                    Before Paid and Done because that is the order it happens in: payroll is told, then
                    the money moves, then the referral is finished. */}
                <td className="table__center">
                  <Flag
                    on={referral.payoutAnnouncedToPayroll}
                    yes="Payroll has been told about this bonus"
                    no="Payroll has not been told yet. The digest sends on its own; nothing to do here."
                  />
                </td>

                <td className="table__center" onClick={(e) => e.stopPropagation()}>
                  <Flag on={referral.isPaid} />

                  {/* The payout date has gone and nothing has been recorded against it. Two buttons,
                      because the application does not pay anybody and cannot know money moved — a
                      person says which way it went, and either answer finishes the row.

                      "Paid" only where HR accepted the bonus: recording a payment against a referral
                      nobody agreed was owed would invent the decision as well as the payment. "Not
                      paid" asks nothing of the record beyond being closed, so it is always offered. */}
                  {awaitingClosure && (
                    <span className="table__close">
                      {referral.eligibleForPayout === true && (
                        <button
                          type="button"
                          className="link-button table__sub"
                          title="The bonus went out. Records it and closes the referral."
                          disabled={busy}
                          onClick={() => onClose(referral, true)}
                        >
                          paid
                        </button>
                      )}
                      <button
                        type="button"
                        className="link-button table__sub"
                        title="No money went out. Closes the referral anyway."
                        disabled={busy}
                        onClick={() => onClose(referral, false)}
                      >
                        not paid
                      </button>
                    </span>
                  )}
                </td>

                <td className="table__center">
                  <Flag on={referral.completed} />
                </td>
                <td className="table__fit" onClick={(e) => e.stopPropagation()}>
                  {/* Only the unlock lives here now. Editing is the row itself, so a pencil beside
                      every row was a second target for the thing the whole row already does.

                      Reopening is not: it is the one action a closed row accepts, and a closed row is
                      exactly the one the click does nothing on — so it keeps a button, and this column
                      shrinks to nothing on every other row. */}
                  {referral.completed && (
                    <div className="table__actions">
                      <button
                        type="button"
                        className="icon-button"
                        title="This referral is closed. Reopen it to change its decision or statuses."
                        aria-label={"Reopen the referral for " + referral.newHireName}
                        disabled={busy}
                        onClick={() => onReopen(referral)}
                      >
                        <UnlockIcon />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Three states rather than a checkbox, because the referral has three: undecided, accepted, turned
 * down. A checkbox can only say yes or no, and an unticked box for "nobody has looked at this yet"
 * reads as a decision that was made — which is exactly the row that then never gets chased.
 *
 * On a finished referral it stops being a control at all. The outcome is on the record by then — the
 * money went out, or it was settled that none would — and both are things other people have acted on,
 * so changing one is Reopen and then decide rather than a stray click on a crowded table.
 */
function EligibilityControl(props: {
  referral: Referral;
  needsDecision: boolean;
  missed: boolean;
  busy: boolean;
  onDecide: (referral: Referral, eligible: boolean | null) => void;
  onResolveMissed: (referral: Referral) => void;
}) {
  const { referral, needsDecision, missed, busy, onDecide, onResolveMissed } = props;

  if (referral.completed) {
    // Three states here too, not two. A row can be closed from the edit form with the decision never
    // made — closed and turned down are not the same thing, and a locked badge reading "✕ No" would
    // put an answer on the record that nobody gave.
    if (referral.eligibleForPayout === null) {
      return (
        <span className="decided decided--locked" title="Closed with no decision on it. Reopen it to decide.">
          — Closed
        </span>
      );
    }

    return (
      <span
        className={
          referral.eligibleForPayout
            ? 'decided decided--locked decided--yes'
            : 'decided decided--locked decided--no'
        }
        title={
          referral.eligibleForPayout
            ? 'The bonus was owed. This referral is closed — reopen it to change that.'
            : 'Turned down. This referral is closed — reopen it to change that.'
        }
      >
        {referral.eligibleForPayout ? '✓ Yes' : '✕ No'}
      </span>
    );
  }

  if (referral.eligibleForPayout === null) {
    // No prompt line beside the buttons: it was the widest thing in this column, so it set the
    // column's width and left the pills in every other row floating in the middle of it. The row's
    // own highlight and the count in the page header say the same thing without the width.
    return (
      <div className="decide__buttons">
        <button
          type="button"
          className={missed ? 'decide__yes decide__yes--asks' : 'decide__yes'}
          title={
            missed
              ? 'Yes — but the run it was due on has gone. Asks what happened.'
              : needsDecision
                ? 'Yes — the bonus is owed'
                : 'Yes — the bonus will be owed'
          }
          disabled={busy}
          onClick={() => (missed ? onResolveMissed(referral) : onDecide(referral, true))}
        >
          ✓
        </button>
        <button
          type="button"
          className="decide__no"
          title="No — this referral does not qualify"
          disabled={busy}
          onClick={() => onDecide(referral, false)}
        >
          ✕
        </button>
      </div>
    );
  }

  // Accepted, and not yet paid. Still a button, so a wrong call can be taken back without opening the
  // edit form. Turning one down is never destructive either — the row stays, marked closed, and
  // reopening it puts the decision back in play.
  return (
    <button
      type="button"
      className="decided decided--yes"
      title="The bonus is owed. Click to undo this decision."
      disabled={busy}
      onClick={() => onDecide(referral, null)}
    >
      ✓ Yes
    </button>
  );
}

/**
 * A column heading that orders the table by its own column.
 *
 * Every column carries an arrow, not only the one in force: a mark that appears once you have already
 * clicked says nothing about the columns you have not tried. Clicking cycles ascending, descending,
 * then back to the urgency order the page loads in — without that third step there would be no way
 * back to the order this page exists to show.
 */
function SortableHeader({
  label,
  columnKey,
  className,
  sort,
  onSort,
}: {
  label: string;
  columnKey: SortKey;
  className?: string;
  sort: Sort;
  onSort: (key: SortKey) => void;
}) {
  const active = sort !== null && sort.key === columnKey;

  return (
    <th
      scope="col"
      className={className}
      aria-sort={active ? (sort.descending ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        className="table__sort"
        onClick={() => onSort(columnKey)}
        title={
          active && sort.descending
            ? 'Back to urgency order'
            : `Sort by ${label.toLowerCase()}`
        }
      >
        {label}
        <span
          className={active ? 'table__sort-arrow' : 'table__sort-arrow table__sort-arrow--idle'}
          aria-hidden="true"
        >
          {active ? (sort.descending ? '▾' : '▴') : '⇅'}
        </span>
      </button>
    </th>
  );
}

/**
 * A yes/no mark in a column of them.
 *
 * The titles are overridable because "Yes" says nothing useful on a column whose whole point is that
 * nobody set it — a flag that cannot be clicked should at least explain itself when hovered.
 */
function Flag({ on, yes = 'Yes', no = 'No' }: { on: boolean; yes?: string; no?: string }) {
  return on ? (
    <span className="flag flag--on" title={yes}>
      ✓
    </span>
  ) : (
    <span className="flag" title={no}>
      —
    </span>
  );
}
